import type { Place, PlaceStatus } from "@couples/shared";
import { useState } from "react";

import { todayDateKey } from "../lib/dates";
import type { PlaceInput } from "../lib/places";
import SheetShell from "./SheetShell";

interface Props {
  /** 編集対象。無ければ新規追加 */
  editingPlace?: Place;
  busy: boolean;
  onSave: (input: PlaceInput) => void;
  onClose: () => void;
}

function statusChipClass(active: boolean): string {
  return active
    ? "bg-[var(--color-accent)] text-white"
    : "border border-[var(--color-line)] text-[var(--color-sub)]";
}

/** 「行ってみたい場所」「行った場所」の追加・編集シート。新規は常に「行きたい」から始まる */
export default function PlaceSheet({ editingPlace, busy, onSave, onClose }: Props) {
  const [name, setName] = useState(editingPlace?.name ?? "");
  const [memo, setMemo] = useState(editingPlace?.memo ?? "");
  const [status, setStatus] = useState<PlaceStatus>(editingPlace?.status ?? "want");
  const [visitedOn, setVisitedOn] = useState(editingPlace?.visitedOn ?? todayDateKey());

  const nameText = name.trim();
  const canSubmit = nameText !== "" && (status === "want" || visitedOn !== "");

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    onSave({
      name: nameText,
      memo: memo.trim() === "" ? null : memo.trim(),
      status,
      visitedOn: status === "visited" ? visitedOn : null,
    });
  }

  return (
    <SheetShell
      title={editingPlace !== undefined ? "場所を編集" : "場所を追加"}
      onClose={onClose}
      tone="bg"
    >
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">名前</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              placeholder="お店・スポットの名前"
              className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">メモ</span>
            <textarea
              value={memo}
              onChange={(event) => {
                setMemo(event.target.value);
              }}
              placeholder="住所・気になる理由など(任意)"
              rows={3}
              className="w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
            />
          </label>

          <div className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">状態</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setStatus("want");
                }}
                aria-pressed={status === "want"}
                className={`flex-1 rounded-lg px-3 py-2 text-sm ${statusChipClass(status === "want")}`}
              >
                行きたい
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatus("visited");
                }}
                aria-pressed={status === "visited"}
                className={`flex-1 rounded-lg px-3 py-2 text-sm ${statusChipClass(status === "visited")}`}
              >
                行った
              </button>
            </div>
          </div>

          {status === "visited" && (
            <label className="block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">行った日</span>
              <input
                type="date"
                value={visitedOn}
                max={todayDateKey()}
                onChange={(event) => {
                  setVisitedOn(event.target.value);
                }}
                className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            </label>
          )}
        </div>

        <div className="border-t border-[var(--color-line)] p-4">
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="w-full rounded-lg bg-[var(--color-accent)] py-2.5 text-sm font-medium text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            {editingPlace !== undefined ? "保存" : "追加"}
          </button>
        </div>
      </form>
    </SheetShell>
  );
}
